import type { Message } from "../models/Message";
import { MessageType, OwnerTypeMessage } from "../models/Message";
import type { Service } from "../models/Service";
import notificationSound from "../assets/sounds/notification.mp3";
import { removeFormattingMarksPreservingSpacing } from "./channelAndHtml";
import { getSpecialMessagePreview } from "./specialMessages";

export type SystemNotificationPermission = NotificationPermission | "unsupported";

export interface MessageNotificationTypePreferences {
  system: boolean;
  sound: boolean;
}

export interface SystemNotificationPayload {
  title: string;
  body: string;
  tag: string;
  onClick?: () => void;
}

const maxNotificationPreviewLength = 140;
const notificationTypePreferencesStorageKey = "Chat@MessageNotificationTypes";
const defaultNotificationTypePreferences: MessageNotificationTypePreferences = {
  system: true,
  sound: true,
};
let notificationAudio: HTMLAudioElement | null = null;

const notificationTypePreferencesKey = ( userId?: string | null ) => (
  userId ? `${ notificationTypePreferencesStorageKey }:${ userId }` : notificationTypePreferencesStorageKey
);

export const getMessageNotificationTypePreferences = ( userId?: string | null ): MessageNotificationTypePreferences => {
  if( typeof window === "undefined" ) return defaultNotificationTypePreferences;

  try {
    const rawPreferences = window.localStorage.getItem( notificationTypePreferencesKey( userId ));
    if( !rawPreferences ) return defaultNotificationTypePreferences;

    const parsedPreferences = JSON.parse( rawPreferences ) as Partial<MessageNotificationTypePreferences>;

    return {
      system: typeof parsedPreferences.system === "boolean" ? parsedPreferences.system : defaultNotificationTypePreferences.system,
      sound: typeof parsedPreferences.sound === "boolean" ? parsedPreferences.sound : defaultNotificationTypePreferences.sound,
    };
  } catch {
    return defaultNotificationTypePreferences;
  }
};

export const setMessageNotificationTypePreferences = ( userId: string | null | undefined, preferences: MessageNotificationTypePreferences ) => {
  if( typeof window === "undefined" ) return;

  try {
    window.localStorage.setItem( notificationTypePreferencesKey( userId ), JSON.stringify( preferences ));
  } catch {
    return;
  }
};

export const systemNotificationsSupported = () => typeof window !== "undefined" && "Notification" in window;

export const getSystemNotificationPermission = (): SystemNotificationPermission => {
  if( !systemNotificationsSupported()) return "unsupported";
  return window.Notification.permission;
};

export const requestSystemNotificationPermission = async (): Promise<SystemNotificationPermission> => {
  if( !systemNotificationsSupported()) return "unsupported";
  if( window.Notification.permission !== "default" ) return window.Notification.permission;

  try {
    return await window.Notification.requestPermission();
  } catch {
    return window.Notification.permission;
  }
};

export const showSystemNotification = ({ title, body, tag, onClick }: SystemNotificationPayload): boolean => {
  if( getSystemNotificationPermission() !== "granted" ) return false;

  try {
    const notification = new window.Notification( title, {
      body,
      tag,
      renotify: true,
      silent: true,
    });

    if( onClick ) {
      notification.onclick = () => {
        window.focus();
        notification.close();
        onClick();
      };
    }

    return true;
  } catch {
    return false;
  }
};

export const playNotificationSound = () => {
  if( typeof window === "undefined" ) return;

  try {
    const audio = notificationAudio ?? new Audio( notificationSound );
    notificationAudio = audio;
    audio.currentTime = 0;
    void audio.play().catch(() => {});
  } catch {
    // Browsers may block audio until the user interacts with the page.
  }
};

const messageTimestamp = ( message: Message ): number => {
  const parsed = message.createdAt ? new Date( message.createdAt ).getTime() : 0;
  return Number.isNaN( parsed ) ? 0 : parsed;
};

const sanitizePreview = ( value: string ) => {
  const withoutHtml = value.replace(/<[^>]+>/g, " ");
  const sanitized = removeFormattingMarksPreservingSpacing( withoutHtml ).replace(/\s+/g, " " ).trim();

  return sanitized.length > maxNotificationPreviewLength
    ? `${ sanitized.slice( 0, maxNotificationPreviewLength - 1 ) }...`
    : sanitized;
};

export const getIncomingMessageNotificationPreview = ( message: Message ): string => {
  const specialPreview = getSpecialMessagePreview( message );
  if( specialPreview ) return sanitizePreview( specialPreview );

  if( message.content ) return sanitizePreview( message.content );

  const fileName = message.fileMetadata?.originalFileName || message.fileMetadata?.fileName;
  if( fileName ) return sanitizePreview( fileName );

  const typePreviewMapper: Record<MessageType, string> = {
    [ MessageType.EMPTY ]: "Nova mensagem",
    [ MessageType.TEXT ]: "Nova mensagem",
    [ MessageType.DOCUMENT ]: "Documento recebido",
    [ MessageType.IMAGE ]: "Imagem recebida",
    [ MessageType.AUDIO ]: "Audio recebido",
    [ MessageType.VIDEO ]: "Video recebido",
    [ MessageType.LOADING ]: "Nova mensagem",
    [ MessageType.STICKER ]: "Figurinha recebida",
    [ MessageType.LOCATION ]: "Localizacao recebida",
    [ MessageType.LIVE_LOCATION ]: "Localizacao em tempo real recebida",
    [ MessageType.CONTACT ]: "Contato recebido",
    [ MessageType.REACTION ]: "Reacao recebida",
    [ MessageType.POLL ]: "Enquete recebida",
    [ MessageType.INTERNAL ]: "Nova mensagem",
  };

  return typePreviewMapper[ message.messageType ];
};

export const getLatestIncomingContactMessage = ( service: Service ): Message | null => {
  const messages = service.chat?.messages ?? [];

  return messages.reduce<Message | null>(( latestMessage, message ) => {
    if( message.ownerType !== OwnerTypeMessage.CONTACT ) return latestMessage;
    if( message.messageType === MessageType.REACTION || message.channelDeletedAt ) return latestMessage;
    if( !latestMessage ) return message;

    return messageTimestamp( message ) > messageTimestamp( latestMessage ) ? message : latestMessage;
  }, null );
};

export const serviceBelongsToUser = ( service: Service, userId?: string | null ): boolean => {
  return Boolean( userId && ( service.user?.id === userId || service.userId === userId ));
};

export const buildIncomingMessageNotification = ( service: Service, message: Message ): SystemNotificationPayload => {
  const contactName = service.chat?.contact?.name || service.chat?.name || message.contact?.name || "Contato";

  return {
    title: `Nova mensagem de ${ contactName }`,
    body: getIncomingMessageNotificationPreview( message ) || "Nova mensagem",
    tag: `chat:${ service.chat?.id || message.chatId || message.id }`,
  };
};
